import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Loader2, PhoneOff, Send } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATES = {
  IDLE:       "idle",
  RECORDING:  "recording",
  PROCESSING: "processing",
  PLAYING:    "playing",
  ERROR:      "error",
  ENDED:      "ended",
};

function getOrCreateClientId() {
  const key = "nova_client_id";
  let id = localStorage.getItem(key);
  if (!id) {
    id = "clt-" + Math.random().toString(36).slice(2, 10) + "-" + Date.now();
    localStorage.setItem(key, id);
  }
  return id;
}

export default function VoiceChat({ agentId = "nova", sessionId = "voice-web" }) {
  const [status, setStatus]         = useState(STATES.IDLE);
  const [transcript, setTranscript] = useState("");
  const [reply, setReply]           = useState("");
  const [errorMsg, setErrorMsg]     = useState("");
  const [history, setHistory]       = useState([]);
  const [callEnded, setCallEnded]   = useState(false);
  const [noMic, setNoMic]           = useState(false);
  const [textInput, setTextInput]   = useState("");

  const mediaRecorderRef = useRef(null);
  const chunksRef        = useRef([]);
  const audioRef         = useRef(null);
  const clientId         = useRef(getOrCreateClientId());

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  const startRecording = async () => {
    if (callEnded) return;
    setStatus(STATES.RECORDING);
    setTranscript("");
    setReply("");
    setErrorMsg("");
    chunksRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm;codecs=opus" });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await sendAudio(blob);
      };

      recorder.start();
    } catch (e) {
      setNoMic(true);
      setStatus(STATES.IDLE);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
      setStatus(STATES.PROCESSING);
    }
  };

  const sendAudio = async (blob) => {
    const form = new FormData();
    form.append("file",       blob, "audio.webm");
    form.append("session_id", sessionId);
    form.append("agent",      agentId.toUpperCase());
    form.append("language",   "fr");
    form.append("client_id",  clientId.current);
    form.append("history",    JSON.stringify(history));

    try {
      const resp = await fetch(`${BACKEND_URL}/api/voice`, {
        method: "POST",
        body: form,
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${resp.status}`);
      }

      const userText  = resp.headers.get("X-Transcript") || "";
      const replyText = resp.headers.get("X-Reply")      || "";
      const isEnd     = resp.headers.get("X-End-Of-Call") === "true";

      setTranscript(userText);
      setReply(replyText);

      setHistory((prev) => [
        ...prev,
        { role: "user",      content: userText  },
        { role: "assistant", content: replyText },
      ]);

      const audioBlob = await resp.blob();
      const url = URL.createObjectURL(audioBlob);

      setStatus(STATES.PLAYING);
      if (audioRef.current) {
        audioRef.current.src = url;
        audioRef.current.onended = () => {
          URL.revokeObjectURL(url);
          if (isEnd) {
            setCallEnded(true);
            setStatus(STATES.ENDED);
          } else {
            setStatus(STATES.IDLE);
          }
        };
        audioRef.current.play();
      }
    } catch (e) {
      setErrorMsg(e.message);
      setStatus(STATES.ERROR);
    }
  };

  const sendText = async () => {
    const msg = textInput.trim();
    if (!msg || isProcessing) return;
    setStatus(STATES.PROCESSING);
    setTranscript(msg);
    setReply("");
    setErrorMsg("");
    setTextInput("");

    const messages = [...history, { role: "user", content: msg }];

    try {
      const resp = await fetch(`${BACKEND_URL}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          agent:      agentId.toUpperCase(),
          messages,
          session_id: sessionId,
        }),
      });

      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

      const data      = await resp.json();
      const replyText = data.response || "";

      setReply(replyText);
      setHistory((prev) => [
        ...prev,
        { role: "user",      content: msg       },
        { role: "assistant", content: replyText },
      ]);
      setStatus(STATES.IDLE);
    } catch (e) {
      setErrorMsg(e.message);
      setStatus(STATES.ERROR);
    }
  };

  const resetCall = () => {
    setHistory([]);
    setCallEnded(false);
    setTranscript("");
    setReply("");
    setErrorMsg("");
    setStatus(STATES.IDLE);
  };

  const isRecording  = status === STATES.RECORDING;
  const isProcessing = status === STATES.PROCESSING || status === STATES.PLAYING;

  return (
    <div className="flex flex-col items-center gap-4 py-4 w-full">
      <audio ref={audioRef} className="hidden" />

      {/* Bouton micro */}
      {!callEnded ? (
        <button
          onMouseDown={startRecording}
          onMouseUp={stopRecording}
          onTouchStart={(e) => { e.preventDefault(); startRecording(); }}
          onTouchEnd={(e)   => { e.preventDefault(); stopRecording(); }}
          disabled={isProcessing}
          className={[
            "relative flex items-center justify-center w-20 h-20 rounded-full transition-all duration-200 select-none",
            isRecording
              ? "bg-red-500 shadow-[0_0_30px_rgba(239,68,68,0.6)] scale-110"
              : isProcessing
              ? "bg-gray-600 cursor-not-allowed"
              : "bg-violet-600 hover:bg-violet-500 shadow-[0_0_20px_rgba(167,139,250,0.4)] hover:scale-105",
          ].join(" ")}
          aria-label={isRecording ? "Relâcher pour envoyer" : "Maintenir pour parler"}
        >
          {isProcessing ? (
            <Loader2 className="w-8 h-8 text-white animate-spin" />
          ) : isRecording ? (
            <MicOff className="w-8 h-8 text-white" />
          ) : (
            <Mic className="w-8 h-8 text-white" />
          )}
          {isRecording && (
            <span className="absolute inset-0 rounded-full animate-ping bg-red-400 opacity-40" />
          )}
        </button>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center justify-center w-20 h-20 rounded-full bg-gray-700">
            <PhoneOff className="w-8 h-8 text-gray-400" />
          </div>
          <button
            onClick={resetCall}
            className="text-xs text-violet-400 hover:text-violet-300 underline underline-offset-2"
          >
            Nouvel appel
          </button>
        </div>
      )}

      {/* Fallback texte si pas de micro */}
      {noMic && !callEnded && (
        <div className="flex w-full max-w-sm gap-2">
          <input
            type="text"
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendText()}
            placeholder="Écrivez votre message…"
            disabled={isProcessing}
            className="flex-1 rounded-lg bg-gray-800 border border-gray-700 px-3 py-2 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-violet-500 disabled:opacity-50"
          />
          <button
            onClick={sendText}
            disabled={isProcessing || !textInput.trim()}
            className="flex items-center justify-center w-10 h-10 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
            aria-label="Envoyer"
          >
            {isProcessing ? (
              <Loader2 className="w-4 h-4 text-white animate-spin" />
            ) : (
              <Send className="w-4 h-4 text-white" />
            )}
          </button>
        </div>
      )}

      {/* Instruction */}
      <p className="text-xs text-gray-400 select-none">
        {callEnded
          ? "Appel terminé — mémoire sauvegardée"
          : isRecording
          ? "Relâchez pour envoyer…"
          : isProcessing
          ? (status === STATES.PLAYING ? `${agentId} répond…` : "Traitement…")
          : noMic
          ? "Microphone indisponible — mode texte actif"
          : "Maintenez pour parler"}
      </p>

      {/* Compteur de tours */}
      {history.length > 0 && (
        <p className="text-[10px] text-gray-600 font-mono">
          {history.length / 2} échange{history.length / 2 > 1 ? "s" : ""}
        </p>
      )}

      {/* Transcription + réponse du tour courant */}
      {transcript && (
        <div className="w-full max-w-sm space-y-2">
          <div className="rounded-lg bg-gray-800 px-3 py-2 text-sm text-gray-300">
            <span className="text-gray-500 text-xs block mb-1">Vous</span>
            {transcript}
          </div>
          {reply && (
            <div className="rounded-lg bg-violet-900/40 border border-violet-700/40 px-3 py-2 text-sm text-violet-100">
              <span className="text-violet-400 text-xs block mb-1">{agentId}</span>
              {reply}
            </div>
          )}
        </div>
      )}

      {/* Erreur */}
      {status === STATES.ERROR && (
        <p className="text-xs text-red-400 max-w-sm text-center">{errorMsg}</p>
      )}
    </div>
  );
}
